import { z } from 'zod';

const RELEASE_VERSION_PATTERN =
    /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*)?(?:\+[0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*)?$/;
const APPLICATION_RELEASE_METADATA_SCHEMA = z.object({
    schemaVersion: z.literal(1),
    environment: z.string().min(1).max(128),
    version: z.string().max(200).regex(RELEASE_VERSION_PATTERN).refine(hasSafeVersionComponents),
    build: z.string().datetime(),
    frontendBuild: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[a-zA-Z0-9._-]+$/),
});

const APPLICATION_RELEASE_INFORMATION_SCHEMA = APPLICATION_RELEASE_METADATA_SCHEMA.pick({
    version: true,
    build: true,
    frontendBuild: true,
});

export type ApplicationReleaseInformation = z.infer<typeof APPLICATION_RELEASE_INFORMATION_SCHEMA>;
export type ApplicationReleaseMetadata = z.infer<typeof APPLICATION_RELEASE_METADATA_SCHEMA>;

/** Validate the release identity embedded in the frontend HTML. */
export function parseApplicationReleaseMetadata(
    value: unknown,
    environment: string
): ApplicationReleaseMetadata {
    const metadata = APPLICATION_RELEASE_METADATA_SCHEMA.parse(value);
    assertEnvironment(metadata.environment, environment);
    return metadata;
}

/** Validate the app Worker's public release response. */
export function parseApplicationReleaseInformation(value: unknown): ApplicationReleaseInformation {
    return APPLICATION_RELEASE_INFORMATION_SCHEMA.parse(value);
}

function assertEnvironment(actual: string, expected: string) {
    if (actual !== expected) {
        throw new Error('Application release belongs to a different environment');
    }
}

function hasSafeVersionComponents(version: string): boolean {
    const coreVersion = version.split(/[+-]/)[0]!;
    return coreVersion.split('.').every((component) => Number.isSafeInteger(Number(component)));
}
